/**
 * Azure Application Insights telemetry singleton.
 *
 * Initialises from VITE_APPINSIGHTS_CONNECTION_STRING at module load time.
 * When the variable is absent or empty the module is a safe no-op — every
 * exported function becomes a silent noop so callers need no guard checks.
 *
 * Global custom dimensions (persona, clinic, role) are attached via a
 * telemetry initializer that reads a live ref at emit-time, so persona
 * switches are reflected on subsequent events without reinitialising the SDK.
 *
 * PII RULE (ACT-1037): Never put member name / MRN / MPI / DOB or any PHI
 * in event names or custom dimensions — only opaque ids/codes.
 *
 * Auto route-tracking is intentionally OFF: the SPA member route is
 * `/members/:id` (`/members/pp:<id>` on the PP branch), so an
 * auto-emitted pageView would carry a member identifier in its URL. Combined
 * with `setAuthenticatedUserContext`, that would make App Insights hold
 * user-attributed, timestamped member-record identifiers — exactly the PHI
 * this module promises never to record. We rely solely on the explicit,
 * PHI-free events below (member_search_executed, member_profile_opened, etc).
 */

import {
  ApplicationInsights,
  type ITelemetryItem,
} from "@microsoft/applicationinsights-web";

// ---------------------------------------------------------------------------
// Global dim ref — updated by setGlobalDimensions() on context changes.
// ---------------------------------------------------------------------------

interface GlobalDims {
  persona: string;
  clinic: string;
  role: string;
  env: string;
  /** Staff email — set from MSAL account username / B2C id_token. Not member PHI. */
  email: string;
}

const globalDimsRef: { current: GlobalDims } = {
  current: {
    persona: "(none)",
    clinic: "(none)",
    role: "(none)",
    email: "(none)",
    // env is set once at module-load time from the compile-time env var.
    // dev/qa/uat/prod all share one App Insights resource so every event
    // must carry this dimension to allow dashboard filtering by environment.
    env: (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? "(none)",
  },
};

/**
 * Call this whenever persona / IPA / role changes (e.g. in CurrentUserProvider
 * or after a persona switch). Dims are read at emit-time by the initializer so
 * this takes effect on the next event without reinitialising App Insights.
 */
export function setGlobalDimensions(dims: Partial<GlobalDims>): void {
  globalDimsRef.current = { ...globalDimsRef.current, ...dims };
}

// ---------------------------------------------------------------------------
// SDK init — exactly one attempt at module load; safe-noop when unconfigured.
// ---------------------------------------------------------------------------

const CONNECTION_STRING = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

let appInsights: ApplicationInsights | null = null;

if (CONNECTION_STRING) {
  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: CONNECTION_STRING,
        // Deliberately OFF — see the PHI note in the module docblock above.
        // Sessions/WAU are derived from the explicit session_started /
        // session_expired events instead of auto pageViews.
        enableAutoRouteTracking: false,
        // Keep unhandled-exception telemetry ON — it surfaces real JS
        // errors in App Insights without requiring explicit catch blocks.
        disableExceptionTracking: false,
      },
    });

    // Attach global custom dimensions to every telemetry item.
    // Read globalDimsRef.current at emit-time so persona/clinic/role changes
    // are reflected without reinitialising the SDK.
    appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      item.data ??= {};
      const dims = globalDimsRef.current;
      item.data["persona"] = dims.persona;
      item.data["clinic"] = dims.clinic;
      item.data["role"] = dims.role;
      item.data["env"] = dims.env;
      item.data["email"] = dims.email;
    });

    appInsights.loadAppInsights();
  } catch {
    // Init failure must never crash the app.
    appInsights = null;
  }
}

// ---------------------------------------------------------------------------
// Public API — all functions are safe noops when appInsights is null.
// ---------------------------------------------------------------------------

/**
 * Associate subsequent telemetry with the authenticated user.
 * Call on successful login with the Entra OID (or B2C sub) — this becomes
 * user_AuthenticatedId in App Insights, the key for WAU dcount().
 *
 * Pass undefined / empty string to clear (on logout).
 */
export function setAuthenticatedUser(oid: string | undefined): void {
  if (!appInsights) return;
  if (oid) {
    // Second arg (accountId) omitted — we don't split user/account for WAU.
    appInsights.setAuthenticatedUserContext(oid, undefined, true);
  } else {
    appInsights.clearAuthenticatedUserContext();
  }
}

/**
 * Emit a named custom event with optional extra properties.
 * Props are merged with the global dims that the telemetry initializer injects.
 *
 * PII RULE: event names and props must contain only opaque codes/ids — no
 * member names, MRNs, MPIs, DOBs, or any other PHI.
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string | number | boolean | undefined>,
): void {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, properties);
}

/**
 * Hook — returns a stable trackEvent function bound to App Insights.
 * Import `useTrackEvent` in components; import `trackEvent` directly in
 * non-component modules (e.g. useMemberAssistantSend).
 */
export function useTrackEvent(): (
  name: string,
  properties?: Record<string, string | number | boolean | undefined>,
) => void {
  // trackEvent is already a stable module-level function (not recreated on
  // re-renders), so we can return it directly — no useCallback needed.
  return trackEvent;
}
